import {
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  setTokens,
  setAccountId,
  clearTokens,
  waitForTelegramInitData,
  getTelegramInitData,
  getAuthPromise,
  setAuthPromise,
  getIsAuthenticating,
  setIsAuthenticating,
  willAccessTokenExpireSoon,
  AuthTokens,
} from "./auth"

import { getRuntimeConfig } from "@/shared/config"
import { applyLatestAccessTokenToCentrifugo } from "@/shared/lib/realtime/centrifugoTokenSync"

/** Pick account UUID from an auth/refresh API response trying common field names. */
function extractAccountId(data: Record<string, unknown>): string | undefined {
  const id =
    data["external_account_id"] ??
    data["account_id"] ??
    data["user_id"] ??
    data["id"]
  return typeof id === "string" && id.length > 0 ? id : undefined
}

// ─────────────────────────────────────────────────────────────────────────────
// URL memo cache
//
// buildUrl() calls getRuntimeConfig() + new URL() on every request.
// For static endpoints (no query params) we compute once after config loads.
// ─────────────────────────────────────────────────────────────────────────────
const _urlCache = new Map<string, string>()

function getCachedUrl(endpoint: string): string | null {
  return _urlCache.get(endpoint) ?? null
}

function setCachedUrl(endpoint: string, url: string): void {
  _urlCache.set(endpoint, url)
}

export function warmApiUrlCache(endpoints: string[]): void {
  // Called after config loads. Pre-builds URLs for hot endpoints so the first
  // bet/trick request doesn't pay the URL-construction cost.
  try {
    const base = getRuntimeConfig().apiBaseUrl
    const origin = base.startsWith("/") ? window.location.origin : undefined
    for (const ep of endpoints) {
      if (_urlCache.has(ep)) continue
      const href = base.endsWith("/")
        ? `${base.slice(0, -1)}${ep}`
        : `${base}${ep.startsWith("/") ? ep : `/${ep}`}`
      const url = origin
        ? new URL(href, origin).toString()
        : new URL(href).toString()
      _urlCache.set(ep, url)
    }
  } catch {
    // Non-critical — requests will build URL normally.
  }
}

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH"

type RequestPriority = "high" | "low" | "auto"

interface RequestOptions {
  method?: RequestMethod
  body?: unknown
  params?: Record<string, string | number | undefined>
  headers?: Record<string, string>
  skipAuth?: boolean // For auth endpoints that don't need Bearer token
  priority?: RequestPriority
  timeoutMs?: number
}

/** Coalesces concurrent refresh calls so we don't hit /refresh-token multiple times. */
let refreshInProgress: Promise<boolean> | null = null

class ApiClient {
  private static readonly ACCESS_TOKEN_REFRESH_WINDOW_SEC = 60
  private static readonly HIGH_PRIORITY_TIMEOUT_MS = 3_000
  private static readonly DEFAULT_TIMEOUT_MS = 10_000
  private static readonly AUTH_TIMEOUT_MS = 3_000

  private get baseUrl(): string {
    return getRuntimeConfig().apiBaseUrl
  }

  // ───────────────────────────────────────────────────────────────────────────
  // URL Building
  // ───────────────────────────────────────────────────────────────────────────

  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | undefined>
  ): string {
    // Fast path: static endpoint with no query params — use memo cache.
    if (!params) {
      const cached = getCachedUrl(endpoint)
      if (cached) return cached
    }

    const base = this.baseUrl

    const join = (a: string, b: string) => {
      const left = a.endsWith("/") ? a.slice(0, -1) : a
      const right = b.startsWith("/") ? b : `/${b}`
      return `${left}${right}`
    }

    const href = join(base, endpoint)

    const url = base.startsWith("/")
      ? new URL(href, window.location.origin)
      : new URL(href)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) url.searchParams.append(key, String(value))
      })
    }

    const result = url.toString()

    // Cache static (param-less) endpoints for subsequent calls.
    if (!params) setCachedUrl(endpoint, result)

    return result
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Authentication
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Authenticate with Telegram initData.
   * POST /v1/auth/telegram with Authorization: tma <initData>
   */
  async authenticateWithTelegram(): Promise<boolean> {
    // Prevent concurrent auth attempts
    if (getIsAuthenticating()) {
      const existingPromise = getAuthPromise()
      if (existingPromise) return existingPromise
    }

    setIsAuthenticating(true)

    const authPromise = (async () => {
      try {
        await waitForTelegramInitData()
        const initData = getTelegramInitData()

        if (!initData) {
          if (import.meta.env.DEV)
            console.warn("[ApiClient] No initData for auth")
          return false
        }

        const url = this.buildUrl("/v1/auth/telegram")

        if (import.meta.env.DEV) {
          console.log(
            "[ApiClient] Authenticating with Telegram, initData length:",
            initData.length
          )
        }

        const response = await this.fetchWithTimeout(
          url,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Tma ${initData}`,
            },
          },
          ApiClient.AUTH_TIMEOUT_MS
        )

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ error: "Auth failed" }))
          console.error("[ApiClient] Auth failed:", response.status, error)
          clearTokens()
          return false
        }

        const data = (await response.json()) as Record<string, unknown>
        const tokens: AuthTokens = {
          access_token: data["access_token"] as string,
          refresh_token: data["refresh_token"] as string,
          access_token_expires_at: data["access_token_expires_at"] as
            | number
            | undefined,
          external_account_id: extractAccountId(data),
        }

        if (!tokens.access_token || !tokens.refresh_token) {
          console.error("[ApiClient] Invalid token response")
          return false
        }

        setTokens(tokens)
        if (tokens.external_account_id) setAccountId(tokens.external_account_id)
        applyLatestAccessTokenToCentrifugo()

        if (import.meta.env.DEV) {
          console.log("[ApiClient] Auth successful, tokens received")
        }

        return true
      } catch (e) {
        console.error("[ApiClient] Auth error:", e)
        return false
      } finally {
        setIsAuthenticating(false)
        setAuthPromise(null)
      }
    })()

    setAuthPromise(authPromise)
    return authPromise
  }

  /**
   * Refresh access token using refresh_token.
   * Coalesces concurrent refresh calls (via refreshInProgress).
   */
  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = getRefreshToken()

    if (!refreshToken) {
      if (import.meta.env.DEV)
        console.warn("[ApiClient] No refresh token available")
      return false
    }

    if (refreshInProgress) {
      return refreshInProgress
    }

    refreshInProgress = this.doRefreshAccessToken(refreshToken)
    try {
      return await refreshInProgress
    } finally {
      refreshInProgress = null
    }
  }

  private async doRefreshAccessToken(refreshToken: string): Promise<boolean> {
    try {
      const url = this.buildUrl("/v1/auth/refresh-token")

      const response = await this.fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        },
        ApiClient.AUTH_TIMEOUT_MS
      )

      if (!response.ok) {
        if (import.meta.env.DEV)
          console.warn("[ApiClient] Token refresh failed:", response.status)
        clearTokens()
        return false
      }

      const data = (await response.json()) as Record<string, unknown>
      const newAccessToken = data["access_token"] as string | undefined
      const newRefreshToken = data["refresh_token"] as string | undefined

      if (!newAccessToken || !newRefreshToken) {
        console.error(
          "[ApiClient] Invalid refresh response: missing access_token or refresh_token"
        )
        clearTokens()
        return false
      }

      const refreshTokens: AuthTokens = {
        access_token: newAccessToken,
        refresh_token: newRefreshToken,
        access_token_expires_at: data["access_token_expires_at"] as
          | number
          | undefined,
        external_account_id: extractAccountId(data),
      }
      setTokens(refreshTokens)
      if (refreshTokens.external_account_id)
        setAccountId(refreshTokens.external_account_id)
      applyLatestAccessTokenToCentrifugo()

      if (import.meta.env.DEV) {
        console.log("[ApiClient] Token refreshed successfully")
      }

      return true
    } catch (e) {
      console.error("[ApiClient] Token refresh error:", e)
      clearTokens()
      return false
    }
  }

  /**
   * Logout - revoke current session.
   */
  async logout(): Promise<void> {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    if (!accessToken || !refreshToken) {
      clearTokens()
      return
    }

    try {
      const url = this.buildUrl("/v1/auth/logout")

      await this.fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        },
        ApiClient.AUTH_TIMEOUT_MS
      )
    } catch (e) {
      console.error("[ApiClient] Logout error:", e)
    } finally {
      clearTokens()
    }
  }

  /**
   * Logout from all sessions.
   */
  async logoutAll(): Promise<void> {
    const accessToken = getAccessToken()

    if (!accessToken) {
      clearTokens()
      return
    }

    try {
      const url = this.buildUrl("/v1/auth/logout-all")

      await this.fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
        ApiClient.AUTH_TIMEOUT_MS
      )
    } catch (e) {
      console.error("[ApiClient] Logout all error:", e)
    } finally {
      clearTokens()
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Request with auto-auth
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Synchronous check: returns the cached access token if it is valid and not
   * close to expiry, so the caller can skip the async ensureAuthenticated() and
   * call fetch() without any await in the hot path.
   *
   * Also triggers a background refresh when the token is within the expiry window,
   * so the next request will have a fresh token ready.
   *
   * Returns null when the token is absent, expired, or a refresh is in progress
   * (caller must fall back to the async ensureAuthenticated()).
   */
  private syncToken(): string | null {
    const token = getAccessToken()
    if (!token) return null
    if (isAccessTokenExpired()) return null
    if (willAccessTokenExpireSoon(ApiClient.ACCESS_TOKEN_REFRESH_WINDOW_SEC)) {
      // Kick off background refresh; don't block the current request.
      void this.refreshAccessToken().catch(() => undefined)
    }
    return token
  }

  private async ensureAuthenticated(): Promise<boolean> {
    // 1. Already have access token
    const accessToken = getAccessToken()
    if (accessToken) {
      if (isAccessTokenExpired()) {
        // Expired token: refresh synchronously before request.
        const refreshed = await this.refreshAccessToken()
        if (refreshed) return true
      } else if (
        willAccessTokenExpireSoon(ApiClient.ACCESS_TOKEN_REFRESH_WINDOW_SEC)
      ) {
        // Token is still valid: don't block current request, refresh in background.
        void this.refreshAccessToken().catch(() => undefined)
      }
      return true
    }

    // 2. Wait for ongoing /telegram auth
    const existingPromise = getAuthPromise()
    if (existingPromise) {
      return existingPromise
    }

    // 3. Have refresh_token but no access_token: try refresh first (avoids /telegram)
    if (getRefreshToken()) {
      const refreshed = await this.refreshAccessToken()
      if (refreshed) return true
    }

    // 4. Need full auth via /telegram
    return this.authenticateWithTelegram()
  }

  /**
   * Restore or establish session on app start.
   * Uses saved tokens and refresh when possible; calls /telegram only when necessary.
   */
  ensureSession(): Promise<boolean> {
    return this.ensureAuthenticated()
  }

  /** Parse successful response body; handles empty body (e.g. DELETE 200). */
  private async parseOkResponse<T>(response: Response): Promise<T> {
    const text = await response.text()
    return (text ? JSON.parse(text) : null) as T
  }

  private resolveTimeoutMs(
    priority: RequestPriority | undefined,
    explicitTimeoutMs: number | undefined
  ): number {
    if (typeof explicitTimeoutMs === "number" && explicitTimeoutMs > 0) {
      return explicitTimeoutMs
    }
    return priority === "high"
      ? ApiClient.HIGH_PRIORITY_TIMEOUT_MS
      : ApiClient.DEFAULT_TIMEOUT_MS
  }

  private async fetchWithTimeout(
    url: string,
    init: RequestInit,
    timeoutMs: number
  ): Promise<Response> {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await fetch(url, { ...init, signal: controller.signal })
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError(408, "Request timeout")
      }
      throw error
    } finally {
      window.clearTimeout(timer)
    }
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = "GET",
      body,
      params,
      headers = {},
      skipAuth = false,
      priority,
      timeoutMs,
    } = options
    const resolvedTimeoutMs = this.resolveTimeoutMs(priority, timeoutMs)

    // ── Auth: sync fast-path ────────────────────────────────────────────────
    // If the token is already in the in-memory cache and not expired we can
    // skip the async ensureAuthenticated() entirely, saving one microtask
    // yield before fetch() fires. This matters most for time-critical actions
    // like bet and trick calls.
    let resolvedToken: string | null = null
    if (!skipAuth) {
      resolvedToken = this.syncToken()
      if (!resolvedToken) {
        // Slow path: token missing, expired, or needs full refresh/re-auth.
        const isAuthed = await this.ensureAuthenticated()
        if (!isAuthed) {
          throw new ApiError(401, "Not authenticated")
        }
        resolvedToken = getAccessToken()
      }
    }

    // Browser fetch does not support GET request bodies.
    // For compatibility, map object body fields into query params for GET requests.
    let requestParams = params
    let requestBody = body

    if (method === "GET" && body !== undefined) {
      const bodyParams: Record<string, string | number | undefined> = {}

      if (typeof body === "object" && body !== null && !Array.isArray(body)) {
        Object.entries(body as Record<string, unknown>).forEach(
          ([key, value]) => {
            if (typeof value === "string" || typeof value === "number") {
              bodyParams[key] = value
            } else if (value !== undefined && value !== null) {
              bodyParams[key] = JSON.stringify(value)
            }
          }
        )
      }

      requestParams = {
        ...(params ?? {}),
        ...bodyParams,
      }
      requestBody = undefined
    }

    const url = this.buildUrl(endpoint, requestParams)

    const requestHeaders: Record<string, string> = {
      ...headers,
    }

    // Set JSON content type only when a request body exists.
    // This avoids unnecessary CORS preflight for body-less requests.
    if (requestBody !== undefined) {
      requestHeaders["Content-Type"] = "application/json"
    }

    // Add Bearer token — reuse the token already resolved above (no extra getAccessToken() call).
    if (!skipAuth && resolvedToken) {
      requestHeaders["Authorization"] = `Bearer ${resolvedToken}`
    }

    const buildInit = (): RequestInit => {
      const init: RequestInit & { priority?: RequestPriority } = {
        method,
        headers: requestHeaders,
        body:
          requestBody !== undefined ? JSON.stringify(requestBody) : undefined,
      }

      if (priority) {
        init.priority = priority
      }

      return init
    }

    const response = await this.fetchWithTimeout(url, buildInit(), resolvedTimeoutMs)

    // Handle 401 - try to refresh token and retry once
    if (response.status === 401 && !skipAuth) {
      if (import.meta.env.DEV) {
        console.log("[ApiClient] Got 401, attempting token refresh...")
      }

      const refreshed = await this.refreshAccessToken()

      if (refreshed) {
        // Retry the request with new token
        const newAccessToken = getAccessToken()
        if (newAccessToken) {
          requestHeaders["Authorization"] = `Bearer ${newAccessToken}`
        }

        const retryResponse = await this.fetchWithTimeout(
          url,
          buildInit(),
          resolvedTimeoutMs
        )

        if (retryResponse.ok) {
          return this.parseOkResponse<T>(retryResponse)
        }

        // If retry also failed with 401, re-authenticate from scratch
        if (retryResponse.status === 401) {
          if (import.meta.env.DEV) {
            console.log(
              "[ApiClient] Retry failed with 401, re-authenticating..."
            )
          }

          clearTokens()
          const reAuthed = await this.authenticateWithTelegram()

          if (reAuthed) {
            const finalToken = getAccessToken()
            if (finalToken) {
              requestHeaders["Authorization"] = `Bearer ${finalToken}`
            }

            const finalResponse = await this.fetchWithTimeout(
              url,
              buildInit(),
              resolvedTimeoutMs
            )

            if (finalResponse.ok) {
              return this.parseOkResponse<T>(finalResponse)
            }

            const error = await finalResponse
              .json()
              .catch(() => ({ message: "Request failed" }))
            throw new ApiError(
              finalResponse.status,
              error.message || error.error || "Request failed",
              error
            )
          }
        }

        const error = await retryResponse
          .json()
          .catch(() => ({ message: "Request failed" }))
        throw new ApiError(
          retryResponse.status,
          error.message || error.error || "Request failed",
          error
        )
      }

      // Refresh failed, try full re-auth
      if (import.meta.env.DEV) {
        console.log("[ApiClient] Refresh failed, attempting full re-auth...")
      }

      const reAuthed = await this.authenticateWithTelegram()

      if (reAuthed) {
        const finalToken = getAccessToken()
        if (finalToken) {
          requestHeaders["Authorization"] = `Bearer ${finalToken}`
        }

        const finalResponse = await this.fetchWithTimeout(
          url,
          buildInit(),
          resolvedTimeoutMs
        )

        if (finalResponse.ok) {
          return this.parseOkResponse<T>(finalResponse)
        }

        const error = await finalResponse
          .json()
          .catch(() => ({ message: "Request failed" }))
        throw new ApiError(
          finalResponse.status,
          error.message || error.error || "Request failed",
          error
        )
      }

      throw new ApiError(401, "Authentication failed")
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Request failed" }))
      throw new ApiError(
        response.status,
        error.message || error.error || "Request failed",
        error
      )
    }

    return this.parseOkResponse<T>(response)
  }

  get<T>(
    endpoint: string,
    params?: Record<string, string | number | undefined>
  ): Promise<T> {
    return this.request<T>(endpoint, { method: "GET", params })
  }

  post<T>(
    endpoint: string,
    body?: unknown,
    params?: Record<string, string | number | undefined>
  ): Promise<T> {
    return this.request<T>(endpoint, { method: "POST", body, params })
  }

  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: "PUT", body })
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" })
  }

  deleteWithBody<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE", body })
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message)
    this.name = "ApiError"
  }
}

export const apiClient = new ApiClient()
